/* Original Evolved implementation. Native Shopify cart and discounts; no app SDK. */
(() => {
  'use strict';
  if (customElements.get('evolved-cart-offer')) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const cartUrl = path => {
    const url = new URL(root() + path, location.origin);
    // Native localization owns the market cookie, shared with cart and checkout.
    return url.pathname + url.search;
  };
  const memory = new Map();
  let work = Promise.resolve();
  let accepting = false;
  let trackingStarted = false;
  let trackingId = '';
  document.addEventListener('visitorConsentCollected', () => {
    if (trackingId) window['ga-disable-' + trackingId] = !consent();
  });
  const enqueue = fn => {
    const next = work.then(fn, fn);
    work = next.catch(() => {});
    return next;
  };
  const consent = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const getStored = (key, persistent = false) => {
    try { return (persistent ? localStorage : sessionStorage).getItem(key); }
    catch (_) { return memory.get(key) || null; }
  };
  const putStored = (key, value, persistent = false) => {
    memory.set(key, value);
    try { (persistent ? localStorage : sessionStorage).setItem(key, value); } catch (_) {}
  };
  const rawRequest = async (path, body) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
    const response = await fetch(cartUrl(path), {
      signal: controller.signal,
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart request failed');
    return data;
    } finally { clearTimeout(timeout); }
  };
  const request = async (path, body) => {
    const data = await rawRequest(path, body);
    return path.startsWith('cart/') && path !== 'cart/add.js' && body?.discount === undefined && Array.isArray(data.items) && window.EvolvedCartDiscounts
      ? window.EvolvedCartDiscounts.restore(data, rawRequest) : data;
  };
  const codes = cart => {
    const result = (cart.discount_codes || []).map(d => d.code);
    for (const d of cart.cart_level_discount_applications || []) {
      if (d.type === 'discount_code') result.push(d.title);
    }
    for (const item of cart.items || []) {
      for (const d of item.line_level_discount_allocations || []) {
        if (d.discount_application?.type === 'discount_code') result.push(d.discount_application.title);
      }
    }
    return [...new Set(result.filter(Boolean))];
  };
  const sections = () => {
    const ids = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: ids } }));
    return [...new Set(ids)].slice(0, 5);
  };
  const refresh = async cart => {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.assign(cartUrl('cart'));
      return;
    }
    const ids = sections();
    if (ids.length) {
      const params = new URLSearchParams({ sections: ids.join(',') });
      const response = await fetch(cartUrl('?' + params), { credentials: 'same-origin', cache: 'no-store' });
      const html = await response.json();
      if (ids.every(id => typeof html[id] === 'string')) {
        cart.sections = html;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        return;
      }
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };
  const startTracking = config => {
    if (!config.analyticsEnabled || config.preview || !consent() || !/^G-[A-Z0-9]+$/.test(config.measurementId || '')) return false;
    if (!trackingStarted) {
      trackingStarted = true;
      trackingId = config.measurementId;
      window['ga-disable-' + trackingId] = false;
      if (typeof window.gtag !== 'function') {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        const tag = document.createElement('script');
        tag.async = true;
        tag.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.measurementId);
        document.head.appendChild(tag);
      }
      window.gtag('config', config.measurementId, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
    }
    return true;
  };
  const emit = (el, action) => {
    const c = el.config;
    el.dataset.lastEvent = action;
    if (!startTracking(c)) return;
    window.gtag('event', `ev_offer_${c.audience}_${el.mode}_${el.arm}_${action}`, {
      send_to: c.measurementId, experiment_id: c.experimentId,
      offer_pair: el.pairId, offer_product: el.product.handle, offer_variant: String(el.variant?.id || ''),
      selection_origin: el.shopperSelected ? 'shopper' : 'assigned',
      offer_percent: el.giftMode ? 0 : el.percent, offer_placement: el.dataset.context,
      currency: c.currency, value: el.variant ? el.expectedPrice() / 100 : 0
    });
  };
  const assignment = (config, mode, pairId) => {
    const key = `ev-offer-arm:${config.experimentId}:${config.audience}:${mode}:${pairId}`;
    try {
      const saved = JSON.parse(getStored(key, true) || 'null');
      if (saved && saved.expires > Date.now() && ['a', 'b'].includes(saved.arm)) return saved.arm;
    } catch (_) {}
    const arm = crypto.getRandomValues(new Uint8Array(1))[0] < 128 ? 'a' : 'b';
    putStored(key, JSON.stringify({ arm, expires: Date.now() + 30 * 86400000 }), true);
    return arm;
  };
  // A separate qualifying item can unlock an offer alongside a Rapi bundle.
  // Native BXGY cannot reuse a source that already receives Rapi's product discount.
  const rapiDiscounted = item => (item.properties?._rapi_box_builder || item.properties?._rapi_bundles) &&
    (item.final_line_price < item.original_line_price || item.total_discount > 0);
  const eligible = (cart, config) => cart.items.some(item => !rapiDiscounted(item) && !item.selling_plan_allocation && !item.properties?._ev_gift_for && !item.properties?._evolved_offer && config.sourceIds.includes(item.product_id) && (!config.sourceVariantIds?.length || config.sourceVariantIds.includes(item.variant_id)));
  const removeTransaction = async token => {
    const cart = await request('cart.js');
    const own = cart.items.filter(item => item.properties?._ev_offer_token === token);
    if (!own.length) return cart;
    return request('cart/update.js', { updates: Object.fromEntries(own.map(item => [item.key, 0])) });
  };

  class EvolvedCartOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      try {
        this.config = JSON.parse(this.querySelector('[data-ev-offer-config]').textContent);
        const c = this.config;
        const qa = c.preview ? new URLSearchParams(location.search) : new URLSearchParams();
        const qaMode = qa.get('evo_offer_test');
        this.mode = ['price', 'product', 'incentive'].includes(qaMode) ? qaMode : c.experiment;
        const canTest = c.preview || (c.analyticsEnabled && consent());
        if (!canTest || !['price', 'product', 'incentive'].includes(this.mode)) this.mode = 'baseline';
        const offers = (c.offers || []).filter(offer => offer?.codeA && offer.product?.variants.some(v => v.available) && !c.cartProductIds.includes(offer.product.id));
        if (!offers.length || (c.minimal && c.acceptedCount > 0)) return;
        if (c.minimal) { this.mode = 'baseline'; offers.splice(1); }
        this.offers = offers;
        this.pairId = offers.slice(0, 2).map(offer => offer.product.id).join('-');
        const giftAvailable = offer => offer?.giftCode && offer.gift?.variants.some(v => String(v.id) === String(offer.giftVariant) && v.available) && !c.cartProductIds.includes(offer.gift.id);
        if (this.mode === 'product' && offers.length < 2) this.mode = 'baseline';
        if (this.mode === 'incentive' && !giftAvailable(offers[0])) this.mode = 'baseline';
        this.arm = this.mode === 'baseline' ? 'a' : c.preview ? (qa.get('evo_offer_arm') === 'b' ? 'b' : 'a') : assignment(c, this.mode, this.pairId);
        this.choiceKey = `ev-offer-choice:${c.experimentId}:${c.audience}:${c.sourceVariantIds?.join('-')}:${this.mode}:${this.arm}`;
        const saved = offers.findIndex(offer => String(offer.product.id) === getStored(this.choiceKey));
        this.shopperSelected = saved >= 0;
        this.selectOffer(saved >= 0 ? saved : this.mode === 'product' && this.arm === 'b' ? 1 : 0);
        this.querySelector('[data-ev-add]').addEventListener('click', () => {
          this.accept();
        }, { signal: this.abort.signal });
        this.querySelector('[data-ev-another]')?.addEventListener('click', () => {
          if (accepting) return;
          this.shopperSelected = true;
          this.selectOffer((this.offerIndex + 1) % this.offers.length);
          putStored(this.choiceKey, String(this.product.id));
          emit(this, 'alternative');
          clearTimeout(this.viewTimer);
          this.viewTimer = setTimeout(() => this.expose(), 1000);
        }, { signal: this.abort.signal });
        this.querySelector('[data-ev-replace-add]')?.addEventListener('click', () => this.accept(true), { signal: this.abort.signal });
        this.querySelector('[data-ev-keep]')?.addEventListener('click', () => {
          if (accepting) return;
          this.clearConfirmation(); this.setButton();
          this.querySelector('[data-ev-add]').focus({ preventScroll: true });
        }, { signal: this.abort.signal });
        this.hidden = false;
        this.prepareOfferTimer(false).catch(() => {});
        document.addEventListener('visibilitychange', () => this.updateOfferTimer(), {signal:this.abort.signal});
        window.addEventListener('storage', event => { if(event.key===this.timerKey && Number(event.newValue)>0){this.deadline=Number(event.newValue)+this.config.timerSeconds*1000;this.updateOfferTimer();} }, {signal:this.abort.signal});
        document.addEventListener('cart:change', event => {
          if (event.detail?.cart && (!eligible(event.detail.cart, c) || (c.minimal && event.detail.cart.items.some(item => item.properties?._evolved_offer)))) {
            this.closeChoices(); this.hidden = true;
          }
        }, { signal: this.abort.signal });
        this.observer = new IntersectionObserver(entries => {
          clearTimeout(this.viewTimer);
          if (entries.some(entry => entry.intersectionRatio >= .5) && document.visibilityState === 'visible') {
            this.viewTimer = setTimeout(() => this.expose(), 1000);
          }
        }, { threshold: [.5] });
        this.observer.observe(this);
        const drawer = this.closest('cart-drawer');
        drawer?.addEventListener('dialog:after-show', () => {
          clearTimeout(this.viewTimer);
          this.viewTimer = setTimeout(() => this.expose(), 1000);
        }, { signal: this.abort.signal });
      } catch (_) { this.hidden = true; }
    }
    disconnectedCallback() {
      this.closeChoices();
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.viewTimer);
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.initialized = false;
    }
    selectOffer(index) {
      this.closeChoices();
      const c = this.config;
      this.offerIndex = index;
      this.offer = this.offers[index];
      this.product = this.offer.product;
      const giftAvailable = this.offer.giftCode && this.offer.gift?.variants.some(v => String(v.id) === String(this.offer.giftVariant) && v.available) && !c.cartProductIds.includes(this.offer.gift.id);
      this.giftMode = Boolean(!c.acceptedCount && giftAvailable && (this.mode === 'incentive' ? this.arm === 'b' : this.mode === 'baseline' && c.incentive === 'gift'));
      this.percent = Number(this.mode === 'price' && this.arm === 'b' ? c.percentB : c.percentA);
      this.code = this.giftMode ? this.offer.giftCode : this.mode === 'price' && this.arm === 'b' ? this.offer.codeB : this.offer.codeA;
      // Product-only codes preserve bundle savings: qualifying items are not reused by a second BXGY rule.
      if (!this.giftMode || c.acceptedCount > 0) this.code = this.mode === 'price' && this.arm === 'b' ? this.offer.nextCodeB : this.offer.nextCodeA;
      if (!this.code || (!this.giftMode && (!Number.isFinite(this.percent) || this.percent < 10 || this.percent > 20))) throw new Error('Invalid offer');
      this.giftVariant = this.offer.gift?.variants.find(v => String(v.id) === String(this.offer.giftVariant) && v.available);
      this.dataset.arm = this.arm; this.dataset.mode = this.mode;
      this.dataset.incentive = this.giftMode ? 'gift' : 'discount';
      this.dataset.product = String(this.product.id);
      this.querySelectorAll('[data-ev-text]').forEach(node => { node.textContent = c.strings[node.dataset.evText] || ''; });
      const benefit = this.querySelector('[data-ev-benefit]');
      if (benefit) benefit.textContent = this.offer.key === 'twist' ? (this.offer.chips || []).join(' · ') : this.offer.heading;
      const chips = this.querySelector('[data-ev-features]'); chips.replaceChildren();
      for (const text of (this.offer.chips || []).slice(0, 3)) {
        const chip = document.createElement('li'); chip.textContent = text; chips.appendChild(chip);
      }
      const link = this.querySelector('[data-ev-product]'); link.textContent = this.product.title; link.href = this.product.url;
      const another = this.querySelector('[data-ev-another]');
      if (another) another.hidden = this.offers.length < 2;
      this.querySelector('[data-ev-options]').replaceChildren();
      this.querySelector('[data-ev-status]').textContent = '';
      this.clearConfirmation(); this.buildOptions(); this.renderVariant();
    }
    async prepareOfferTimer(start = false, cart = null) {
      if (!this.config.minimal || !this.config.timerSeconds) return true;
      if (this.timerPromise) {
        await this.timerPromise;
        if (!start || this.deadline) return !this.timerExpired;
      }
      this.timerPromise = (async () => {
        if (!this.timerKey) {
          const current = cart || await rawRequest('cart.js');
          if (!current.token) throw Error('Cart identity unavailable');
          const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(current.token).split('?')[0]));
          this.timerKey = 'ev-offer-start-v1:' + [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2,'0')).join('');
        }
        const stored = getStored(this.timerKey, true);
        let startedAt = Number(stored);
        if (stored === null) {
          const legacy = Number(getStored(this.timerKey.replace('ev-offer-start-v1:', 'ev-offer-deadline-v1:'), true));
          if (legacy > 0) { startedAt = legacy - 600000; putStored(this.timerKey, String(startedAt), true); }
        }
        if (!startedAt && start) {
          startedAt = Date.now();
          putStored(this.timerKey, String(startedAt), true);
          putStored('ev-offer-active-timer-v1', this.timerKey, true);
        }
        if (startedAt) {
          putStored('ev-offer-active-timer-v1', this.timerKey, true);
          this.deadline = startedAt + this.config.timerSeconds * 1000;
          this.updateOfferTimer();
          if (this.isConnected && !this.timerInterval && !this.timerExpired) this.timerInterval = setInterval(() => this.updateOfferTimer(), 1000);
        }
        return !this.timerExpired;
      })();
      try { return await this.timerPromise; } finally { this.timerPromise = null; }
    }
    updateOfferTimer() {
      if (!this.deadline) return;
      const seconds = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
      const timer = this.querySelector('[data-ev-timer]');
      if (timer) {
        const time = Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2,'0');
        this.querySelector('[data-ev-countdown]').textContent = time;
        timer.setAttribute('aria-label', (this.config.strings.timerLabel || 'Offer ends in [time]').replace('[time]', time));
        timer.title = timer.getAttribute('aria-label');
        timer.hidden = false;
      }
      if (!seconds) {
        this.timerExpired = true;
        clearInterval(this.timerInterval);
        const button = this.querySelector('[data-ev-add]');
        if (button?.getAttribute('aria-busy') !== 'true') { this.hidden = true; button.disabled = true; }
      }
    }
    clearConfirmation() {
      this.pendingReplacement = null;
      const panel = this.querySelector('[data-ev-replacement]');
      if (panel) panel.hidden = true;
    }
    isProtection(item) { return [this.config.protectionProductId, this.config.legacyProtectionProductId].filter(Boolean).map(String).includes(String(item.product_id)); }
    netLine(item) {
      const original = item.original_line_price ?? item.original_price * item.quantity;
      return Number.isFinite(original) && Number.isFinite(item.total_discount) && item.total_discount >= 0 ? Math.min(item.final_line_price, original - item.total_discount) : item.final_line_price;
    }
    merchandiseTotal(cart) { return cart.total_price - cart.items.filter(item => this.isProtection(item)).reduce((sum, item) => sum + this.netLine(item), 0); }
    signature(cart) {
      return JSON.stringify({ currency: cart.currency, total: this.merchandiseTotal(cart), codes: codes(cart).sort(), items: cart.items.filter(item => !this.isProtection(item)).map(item => [item.variant_id, item.quantity, item.properties || {}, item.selling_plan_allocation?.selling_plan?.id || null]) });
    }
    offerValid(cart, token) {
      const added = cart.items.find(item => item.properties?._ev_offer_token === token && item.variant_id === this.variant.id);
      const gift = this.giftMode ? cart.items.find(item => item.variant_id === this.giftVariant.id) : null;
      return (!cart.currency || cart.currency === this.config.currency) && Number.isFinite(cart.total_price) && added && eligible(cart, this.config) && added.quantity === 1 && this.netLine(added) <= this.expectedPrice() + 1 && (!this.giftMode || gift && this.netLine(gift) === 0);
    }
    savingsLost(before, after) {
      return before.items.filter(item => !this.isProtection(item)).reduce((sum, old) => {
        const same = after.items.find(item => item.variant_id === old.variant_id && JSON.stringify(item.properties) === JSON.stringify(old.properties));
        return sum + (same && same.quantity === old.quantity ? Math.max(0, this.netLine(same) - this.netLine(old)) : 0);
      }, 0);
    }
    expectedPrice(variant = this.variant) { return this.giftMode ? variant.price : variant.price - Math.floor(variant.price * this.percent / 100); }
    savingPercent(variant = this.variant) {
      if (!variant) return 0;
      const reference = Number.isFinite(variant.compareAtPrice) && variant.compareAtPrice > variant.price ? variant.compareAtPrice : variant.price;
      return reference > 0 ? Math.max(0, Math.min(100, Math.round((reference - this.expectedPrice(variant)) / reference * 100))) : 0;
    }
    money(cents) { return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100); }
    closeChoices(restoreFocus = false) {
      this.floatingAbort?.abort();
      this.floatingAbort = null;
      const body = this.choiceBody;
      if (body?.hasAttribute('popover')) {
        if (body.matches(':popover-open')) body.hidePopover();
        body.removeAttribute('popover');
        for (const key of ['left', 'top', 'width', 'max-height']) body.style.removeProperty(key);
      }
      if (this.disclosure) {
        this.disclosure.open = false;
        this.disclosure.removeAttribute('data-ev-floating');
      }
      this.summary?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.summary?.focus({ preventScroll: true });
    }
    openFloatingChoices(keyboard) {
      const body = this.choiceBody, summary = this.summary;
      body.setAttribute('popover', 'auto');
      this.disclosure.setAttribute('data-ev-floating', '');
      this.disclosure.open = true;
      const anchor = summary.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const width = Math.min(320, viewportWidth - 24);
      const drawerBody = this.closest('cart-drawer')?.shadowRoot?.querySelector('[part="body"]');
      const bounds = drawerBody?.getBoundingClientRect();
      const topEdge = Math.max(12, (bounds?.top || 0) + 8);
      const bottomEdge = Math.min(window.innerHeight - 12, bounds?.bottom || window.innerHeight - 12);
      const below = Math.max(0, bottomEdge - anchor.bottom - 6);
      const above = Math.max(0, anchor.top - topEdge - 6);
      const rtl = getComputedStyle(this).direction === 'rtl';
      body.style.width = width + 'px';
      body.style.left = Math.max(12, Math.min(rtl ? anchor.left : anchor.right - width, viewportWidth - width - 12)) + 'px';
      body.style.top = (anchor.bottom + 6) + 'px';
      body.showPopover();
      const height = body.getBoundingClientRect().height;
      // Never trap a second scroll area in the drawer. If the full panel cannot
      // fit beside its anchor, use the drawer's existing scroll area instead.
      if (height > Math.max(below, above)) {
        this.closeChoices();
        this.disclosure.open = true;
        summary.setAttribute('aria-expanded', 'true');
        if (keyboard) body.querySelector('input:checked:not(:disabled)')?.focus();
        return;
      }
      const top = below >= height || below >= above ? anchor.bottom + 6 : anchor.top - height - 6;
      body.style.top = Math.max(topEdge, Math.min(top, bottomEdge - height)) + 'px';
      summary.setAttribute('aria-expanded', 'true');
      this.floatingAbort = new AbortController();
      const signal = this.floatingAbort.signal;
      window.addEventListener('resize', () => this.closeChoices(), { signal });
      document.addEventListener('scroll', event => {
        if (event.target !== body && !body.contains(event.target instanceof Node ? event.target : null)) this.closeChoices();
      }, { capture: true, passive: true, signal });
      this.closest('cart-drawer')?.addEventListener('dialog:before-hide', () => this.closeChoices(), { signal });
      if (keyboard) (body.querySelector('input:checked:not(:disabled)') || body.querySelector('input:not(:disabled)'))?.focus({ preventScroll: true });
    }
    buildOptions() {
      if (this.config.minimal) { this.buildCompactOptions(); return; }
      this.available = this.product.variants.filter(v => v.available);
      this.selected = [...this.available[0].options];
      this.modelIndex = this.product.options.findIndex(name => /model/i.test(name));
      this.needsModel = this.modelIndex >= 0 && !this.product.fitModel;
      if (this.needsModel) this.selected[this.modelIndex] = '';
      this.controls = [];
      const holder = this.querySelector('[data-ev-options]');
      const disclosure = document.createElement('details');
      disclosure.className = 'ev-choice-disclosure ev-offer__configuration';
      const summary = document.createElement('summary');
      summary.setAttribute('aria-label', this.config.strings.options);
      const selectedText = document.createElement('span');
      selectedText.dataset.evChoiceSelected = '';
      const change = document.createElement('span');
      change.className = 'ev-choice-change';
      change.append(document.createTextNode(this.config.strings.change));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '10'); svg.setAttribute('height', '7');
      svg.setAttribute('viewBox', '0 0 10 7'); svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm1 1 4 4 4-4'); path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.5'); path.setAttribute('fill', 'none');
      svg.appendChild(path); change.appendChild(svg); summary.append(selectedText, change);
      const body = document.createElement('div');
      body.className = 'ev-choice-body';
      disclosure.append(summary, body); holder.appendChild(disclosure);
      this.disclosure = disclosure; this.summary = summary; this.selectedText = selectedText;
      const uid = crypto.randomUUID();
      body.id = 'ev-choice-' + uid;
      body.setAttribute('role', 'group');
      body.setAttribute('aria-label', this.config.strings.options);
      this.choiceBody = body;
      summary.setAttribute('aria-controls', body.id);
      summary.setAttribute('aria-expanded', 'false');
      this.product.options.forEach((name, index) => {
        if (this.product.variants.length === 1 && this.product.variants[0].title === 'Default Title') return;
        if (this.product.fitModel && index === this.modelIndex) return;
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'ev-offer__option';
        const caption = document.createElement('legend');
        caption.className = 'ev-offer__option-label';
        const optionName = /battery|capacity/i.test(name) ? this.config.strings.capacity : name;
        caption.textContent = optionName;
        const choices = document.createElement('div');
        const isColor = /colou?r|finish/i.test(name);
        choices.className = isColor ? 'ev-offer__colors' : 'popover-listbox ev-offer__choices';
        if (/battery|capacity/i.test(name)) choices.classList.add('ev-offer__choices--capacity');
        const inputs = [];
        for (const value of [...new Set(this.product.variants.map(v => v.options[index]))]) {
          const variant = this.available.find(v => v.options[index] === value);
          const label = document.createElement('label');
          label.className = 'ev-offer__choice ' + (isColor ? 'thumbnail-swatch ev-offer__color' : 'popover-listbox__option');
          if (isColor) label.title = value;
          const input = document.createElement('input');
          input.type = 'radio'; input.className = 'sr-only'; input.name = 'ev-offer-' + uid + '-' + index;
          input.value = value; input.dataset.evOption = index;
          input.setAttribute('aria-label', value);
          input.disabled = !variant;
          label.appendChild(input);
          const text = document.createElement('span');
          text.textContent = /battery|capacity/i.test(name) ? value.split(' — ')[0] : value;
          if (isColor && variant?.image) {
            const image = document.createElement('img');
            image.src = variant.image; image.alt = ''; image.width = 50; image.height = 50; image.loading = 'lazy';
            label.appendChild(image); text.className = 'sr-only';
          }
          label.appendChild(text); choices.appendChild(label); inputs.push(input);
          input.addEventListener('change', () => {
            if (!input.checked) return;
            this.selected[index] = input.value;
            const compatible = this.available.filter(v => v.options[index] === input.value);
            const best = compatible.find(v => v.options.every((value, i) => !this.selected[i] || value === this.selected[i])) || compatible[0];
            if (best) {
              const waitingForModel = this.needsModel && !this.selected[this.modelIndex];
              this.selected = [...best.options];
              if (waitingForModel) this.selected[this.modelIndex] = '';
            }
            this.querySelector('[data-ev-status]').textContent = '';
            this.clearConfirmation(); this.renderVariant();
            if (this.variant) this.closeChoices(true);
          }, { signal: this.abort.signal });
        }
        fieldset.append(caption, choices); body.appendChild(fieldset);
        const control = { index, name: optionName, caption, inputs, page: 0, pageSize: isColor ? 5 : 4 };
        if (inputs.length > control.pageSize) {
          const pager = document.createElement('div');
          pager.className = 'ev-choice-pages';
          const previous = document.createElement('button'), next = document.createElement('button'), count = document.createElement('span');
          previous.type = next.type = 'button';
          previous.textContent = '‹'; next.textContent = '›';
          previous.setAttribute('aria-label', 'Previous ' + optionName.toLowerCase() + ' options');
          next.setAttribute('aria-label', 'More ' + optionName.toLowerCase() + ' options');
          count.setAttribute('aria-live', 'polite');
          pager.append(previous, count, next); fieldset.append(pager);
          control.pager = { previous, next, count };
          const move = delta => {
            control.page = Math.max(0, Math.min(Math.ceil(inputs.length / control.pageSize) - 1, control.page + delta));
            this.showOptionPage(control);
          };
          previous.addEventListener('click', () => move(-1), { signal: this.abort.signal });
          next.addEventListener('click', () => move(1), { signal: this.abort.signal });
        }
        this.controls.push(control);
      });
      holder.hidden = !this.controls.length;
      disclosure.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !disclosure.open) return;
        event.preventDefault(); event.stopPropagation(); this.closeChoices(true);
      }, { signal: this.abort.signal });
      disclosure.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(disclosure.open)), { signal: this.abort.signal });
      body.addEventListener('toggle', event => {
        if (event.newState === 'closed' && body.hasAttribute('popover')) this.closeChoices();
      }, { signal: this.abort.signal });
      summary.addEventListener('click', event => {
        if (summary.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
        if (body.hasAttribute('popover')) { event.preventDefault(); this.closeChoices(true); return; }
        if (typeof body.showPopover === 'function' && this.getBoundingClientRect().width >= 540) {
          event.preventDefault(); this.openFloatingChoices(event.detail === 0);
        }
      }, { signal: this.abort.signal });
      const fit = this.querySelector('[data-ev-fit]');
      fit.hidden = !this.product.fitModel;
      fit.textContent = this.product.fitModel ? this.config.strings.fit + ': ' + this.product.fitModel : '';
    }
    compactOptionLabel(value, name) {
      const short = value.split(' — ')[0];
      if (/battery|capacity/i.test(name)) {
        const capacity = short.replace(/[,\s]/g, '').match(/^(\d+(?:\.\d+)?)mAh/i);
        if (capacity && this.offer.key === 'slim') {
          if (Number(capacity[1]) === 5000) return 'Slim & light';
          if (Number(capacity[1]) === 10000) return 'More power';
        }
        if (capacity && Number(capacity[1]) >= 1000) return Number(capacity[1]).toLocaleString(this.config.locale) + ' mAh';
      }
      if (/colou?r/i.test(name)) {
        const colors = short.match(/\b(gray|grey|black|white|lavender|blue|pink|purple|orange|green|red|silver|gold|sand)\b/gi);
        if (colors?.length) return colors[colors.length - 1];
      }
      return short;
    }
    buildCompactOptions() {
      this.available = this.product.variants.filter(v => v.available);
      this.selected = [...this.available[0].options];
      this.controls = [];
      const holder = this.querySelector('[data-ev-options]');
      holder.replaceChildren();
      holder.id = 'ev-offer-options-' + crypto.randomUUID();
      this.product.options.forEach((name, index) => {
        const values = [...new Set(this.available.map(v => v.options[index]))];
        if (values.length < 2) return;
        const label = document.createElement('label');
        label.className = 'ev-offer__select-label';
        const caption = document.createElement('span');
        caption.className = 'sr-only';
        caption.textContent = /battery|capacity/i.test(name) ? this.config.strings.capacity : name;
        const select = document.createElement('select');
        select.setAttribute('aria-label', caption.textContent);
        for (const value of values) {
          const option = document.createElement('option');
          option.value = value; option.textContent = this.compactOptionLabel(value, name); option.title = value.split(' — ')[0];
          select.appendChild(option);
        }
        select.value = this.selected[index];
        select.addEventListener('change', () => {
          this.selected[index] = select.value;
          const candidates = this.available.filter(v => v.options[index] === select.value);
          const next = candidates.find(v => v.options.every((value, i) => value === this.selected[i])) || candidates[0];
          if (!next) return;
          this.selected = [...next.options];
          this.clearConfirmation();
          this.querySelector('[data-ev-status]').textContent = '';
          this.renderVariant();
        }, {signal:this.abort.signal});
        label.append(caption, select); holder.appendChild(label);
        this.controls.push({index, name, select, inputs:[], caption});
      });
      holder.hidden = this.controls.length === 0;
      this.selectedText = document.createElement('span');
      this.summary = document.createElement('span');
    }
    showOptionPage(control, followSelection = false) {
      if (!control.pager) return;
      const { inputs, pageSize, pager } = control;
      const selectedIndex = inputs.findIndex(input => input.checked);
      if (followSelection && selectedIndex >= 0) control.page = Math.floor(selectedIndex / pageSize);
      const pages = Math.ceil(inputs.length / pageSize);
      inputs.forEach((input, i) => { input.parentElement.hidden = Math.floor(i / pageSize) !== control.page; });
      pager.previous.disabled = control.page === 0;
      pager.next.disabled = control.page === pages - 1;
      pager.count.textContent = (control.page + 1) + ' / ' + pages;
    }
    setButton(state = 'ready') {
      const button = this.querySelector('[data-ev-add]');
      const busy = state === 'adding';
      const added = state === 'added';
      button.disabled = busy || added || !this.variant || Boolean(this.timerExpired);
      button.hidden = Boolean(this.pendingReplacement);
      for (const selector of ['[data-ev-another]', '[data-ev-keep]', '[data-ev-replace-add]']) { const node = this.querySelector(selector); if (node) node.disabled = busy || added; }
      button.toggleAttribute('data-added', added);
      if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
      this.querySelector('[data-ev-button-label]').textContent = this.config.strings[busy ? 'adding' : added ? 'added' : !this.variant ? 'chooseModel' : 'add'];
      if (!this.config.minimal && !busy && !added && this.variant && !this.giftMode && this.config.strings.addSaving) this.querySelector('[data-ev-button-label]').textContent = this.config.strings.addSaving.replace('[percent]', this.savingPercent());
      if (this.config.minimal) {
        button.setAttribute('aria-label', (added ? this.config.strings.added : 'Add to cart') + ': ' + this.product.title + ', ' + this.selected.filter(Boolean).map((value, index) => this.compactOptionLabel(value, this.product.options[index])).join(', '));
        this.controls.forEach(control => { control.select.disabled = busy || added; });
      }
      this.querySelector('[data-ev-add-icon]').hidden = added;
      this.querySelector('[data-ev-added-icon]').hidden = !added;
      this.controls.forEach(({index, inputs}) => { inputs.forEach(input => { input.disabled = busy || added || !this.available.some(v => v.options[index] === input.value); }); });
      this.summary?.setAttribute('aria-disabled', String(busy || added));
      if ((busy || added) && this.disclosure) this.disclosure.open = false;
    }
    renderVariant() {
      this.controls.forEach(control => {
        const {index, name, caption, inputs} = control;
        if (control.select) control.select.value = this.selected[index];
        inputs.forEach(input => { input.checked = input.value === this.selected[index]; });
        caption.textContent = name + (this.selected[index] ? ': ' + this.selected[index].split(' — ')[0] : '');
        this.showOptionPage(control, true);
      });
      this.selectedText.textContent = this.needsModel && !this.selected[this.modelIndex] ? this.config.strings.chooseModel : this.selected.filter((value, index) => !(this.product.fitModel && index === this.modelIndex)).map(value => value.split(' — ')[0]).join(' · ');
      this.summary.setAttribute('aria-label', this.config.strings.options + ': ' + this.selectedText.textContent);
      this.variant = this.available.find(v => v.options.every((value, i) => value === this.selected[i]));
      const shown = this.variant || this.available.find(v => v.options.every((value, i) => !this.selected[i] || value === this.selected[i])) || this.available[0];
      const c = this.config;
      const image = this.querySelector('[data-ev-image]');
      image.hidden = !shown.image;
      if (shown.image) image.src = shown.image;
      image.alt = this.product.title + (shown.title === 'Default Title' ? '' : ' — ' + shown.title);
      const formatted = this.giftMode ? shown.formattedBase : this.mode === 'price' && this.arm === 'b' ? shown.formattedB : shown.formattedA;
      this.querySelector('[data-ev-price]').textContent = formatted || this.money(this.expectedPrice(shown));
      const hasCompare = shown.compareAtPrice > shown.price && shown.compareAtPrice > this.expectedPrice(shown);
      this.querySelector('[data-ev-compare]').hidden = !hasCompare;
      this.querySelector('[data-ev-was]').textContent = shown.formattedCompare || this.money(shown.compareAtPrice || 0);
      const badge = this.querySelector('[data-ev-badge]');
      badge.hidden = !hasCompare;
      badge.textContent = hasCompare ? c.strings.save.replace('[percent]', this.savingPercent(shown)) : '';
      const saving = this.querySelector('[data-ev-saving]');
      saving.hidden = false;
      saving.textContent = this.giftMode ? c.strings.giftOffer : c.strings.saving.replace('[percent]', this.percent);
      this.querySelector('[data-ev-gift]').hidden = !this.giftMode;
      if (this.giftMode) {
        this.querySelector('[data-ev-gift-label]').textContent = c.strings.freeGift.replace('[product]', this.offer.giftTitle || this.offer.gift.title);
        const giftImage = this.querySelector('[data-ev-gift-image]');
        giftImage.hidden = !this.giftVariant.image;
        if (this.giftVariant.image) giftImage.src = this.giftVariant.image;
      }
      this.querySelector('[data-ev-terms]').textContent = this.giftMode ? c.strings.giftTerms : c.strings.terms;
      this.setButton();
    }
    expose() {
      if (!this.isConnected || this.hidden || document.visibilityState !== 'visible') return;
      const drawer = this.closest('cart-drawer');
      if (drawer && !drawer.hasAttribute('open')) return;
      const rect = this.getBoundingClientRect();
      const visibleArea = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0)) * Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
      if (!rect.width || !rect.height || visibleArea < rect.width * rect.height * .5 || getComputedStyle(this).visibility === 'hidden') return;
      this.prepareOfferTimer(true).catch(() => { this.querySelector('[data-ev-timer]')?.setAttribute('hidden',''); });
      const key = `ev-offer-view:${this.config.experimentId}:${this.config.audience}:${this.mode}:${this.arm}:${this.pairId}:${this.product.id}`;
      if (getStored(key)) return;
      if (this.config.preview) { this.dataset.lastEvent = 'preview_view'; return; }
      if (!startTracking(this.config)) return;
      putStored(key, '1');
      emit(this, 'view');
      const assignmentValue = { id: this.config.experimentId, audience: this.config.audience, mode: this.mode, arm: this.arm, pair: this.pairId, product: this.product.handle, selection: this.shopperSelected ? 'shopper' : 'assigned', incentive: this.giftMode ? 'gift' : this.percent, placement: this.dataset.context };
      enqueue(async () => {
        const cart = await request('cart.js');
        if (!eligible(cart, this.config)) return;
        let tests = {};
        try { tests = JSON.parse(cart.attributes?.['Evolved experiments'] || '{}'); } catch (_) {}
        tests[this.config.audience] = assignmentValue;
        await request('cart/update.js', { attributes: { 'Evolved experiments': JSON.stringify(tests) } });
      }).catch(() => { this.dataset.attribution = 'failed'; });
    }
    async accept(replace = false) {
      if (this.timerExpired || accepting || window.EvolvedMystery?.isBusy() || !this.variant?.available || (replace && !this.pendingReplacement)) return;
      accepting = true;
      await window.EvolvedShippingProtection?.pause('offer');
      const c = this.config;
      const quote = replace ? this.pendingReplacement : null;
      let succeeded = false;
      const status = this.querySelector('[data-ev-status]');
      this.setButton('adding'); status.textContent = '';
      emit(this, 'click');
      try {
        await enqueue(async () => {
          const token = crypto.randomUUID();
          let before, priorCodes, changedCodes = false, attemptedAdd = false;
          try {
            before = await request('cart.js');
            if (!(await this.prepareOfferTimer(true, before))) throw new Error('expired');
            if (before.currency && before.currency !== c.currency) throw new Error('currency');
            if (before.items.filter(item => item.properties?._evolved_offer).length >= (c.minimal ? 1 : 5) || !eligible(before, c) || before.items.some(item => item.product_id === this.product.id)) throw new Error('unavailable');
            priorCodes = codes(before);
            // Keep the original discounts and their server-side conditions.
            const welcomeGift = priorCodes.some(code => code.toUpperCase() === 'WELCOMEGIFT');
            if (quote && this.signature(before) !== quote.signature) throw new Error('changed');
            const retainedOfferCodes = priorCodes.filter(code => code.toUpperCase().startsWith('EV-CART-') && !before.discount_codes?.some(entry => entry.code.toUpperCase() === code.toUpperCase() && entry.applicable === false));
            const customerCodes = priorCodes.filter(code => !code.toUpperCase().startsWith('EV-CART-'));
            const nextCodes = [...new Set([...retainedOfferCodes, ...(replace ? [] : customerCodes), this.code])];
            // Attach the native code before adding the offer, so Shopify prices the add.
            changedCodes = true;
            await request('cart/update.js', { discount: nextCodes.join(',') });
            const properties = { _evolved_offer: c.audience, _ev_offer_token: token, _ev_offer_code: this.code, _ev_offer_source_product: String(c.sourceIds[0]) };
            if (c.preview) properties._ev_preview = '1';
            if (consent() && c.analyticsEnabled && !c.preview) {
              properties._ev_experiment = c.experimentId;
              properties._ev_arm = this.arm;
              properties._ev_mode = this.mode;
              properties._ev_placement = this.dataset.context;
              properties._ev_pair = this.pairId;
              properties._ev_choice = this.shopperSelected ? 'shopper' : 'assigned';
            }
            const items = [{ id: this.variant.id, quantity: 1, properties }];
            if (this.giftMode && !before.items.some(item => item.variant_id === this.giftVariant.id)) {
              items.push({ id: this.giftVariant.id, quantity: 1, properties: { _ev_offer_token: token, _ev_gift_for: String(this.product.id) } });
            }
            attemptedAdd = true;
            await request('cart/add.js', { items });
            const after = await request('cart.js');
            if (after.currency && after.currency !== c.currency) throw new Error('currency');
            const charged = after.items.find(item => item.properties?._ev_offer_token === token && item.variant_id === this.variant.id);
            if (charged && Number.isFinite(charged.original_price) && Math.abs(charged.original_price - this.variant.price) > 1) throw new Error('currency');
            const lost = this.savingsLost(before, after);
            const increased = !Number.isFinite(before.total_price) || this.merchandiseTotal(after) - this.merchandiseTotal(before) > this.expectedPrice() + 1;
            const valid = this.offerValid(after, token);
            const previousOffers = { items: before.items.filter(item => item.properties?._evolved_offer) };
            if (this.savingsLost(previousOffers, after) > 1) throw new Error('conflict');
            if (quote) {
              if (!valid || this.merchandiseTotal(after) > quote.merchandiseTotal + 1) throw new Error('changed');
            } else if (!valid || lost > 1 || increased) {
              // Never quote a paid welcome gift by temporarily dropping its code.
              if (welcomeGift) throw new Error('conflict');
              const replaceable = priorCodes.filter(code => !code.toUpperCase().startsWith('EV-CART-'));
              if (!replaceable.length) throw new Error('conflict');
              const quoted = await request('cart/update.js', { discount: [...new Set([...retainedOfferCodes, this.code])].join(',') });
              if (!this.offerValid(quoted, token) || this.savingsLost(previousOffers, quoted) > 1) throw new Error('conflict');
              const loss = this.savingsLost(before, quoted);
              await removeTransaction(token); attemptedAdd = false;
              await request('cart/update.js', { discount: priorCodes.join(',') }); changedCodes = false;
              this.pendingReplacement = { signature: this.signature(before), total: quoted.total_price, merchandiseTotal: this.merchandiseTotal(quoted) };
              const detail = c.strings.replaceDetail.replace('[codes]', replaceable.join(', ')).replace('[total]', this.money(this.merchandiseTotal(quoted))).replace('[difference]', loss > 1 ? c.strings.originalDiscount.replace('[amount]', this.money(loss)) : c.strings.orderTotal);
              this.querySelector('[data-ev-replacement-detail]').textContent = detail;
              this.querySelector('[data-ev-replacement]').hidden = false;
              status.textContent = '';
              emit(this, 'discount_choice');
              return;
            }
            this.clearConfirmation();
            succeeded = true;
            this.setButton('added');
            this.hidden = true;
            emit(this, 'accept');
            try { await refresh(after); } catch (_) { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); }
          } catch (error) {
            let safe = true;
            try {
              if (attemptedAdd) await removeTransaction(token);
              if (changedCodes) await request('cart/update.js', { discount: priorCodes.join(',') });
            } catch (_) { safe = false; }
            this.clearConfirmation();
            status.textContent = safe ? (error.message === 'changed' ? c.strings.changed : error.message === 'currency' ? c.strings.currencyError : error.message === 'conflict' ? c.strings.conflict : error.message === 'unavailable' ? c.strings.unavailable : c.strings.error) : c.strings.review;
            emit(this, 'error');
            if (!safe) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        });
      } finally {
        accepting = false;
        this.setButton(succeeded ? 'added' : 'ready');
        if (this.timerExpired) this.hidden = true;
        window.EvolvedShippingProtection?.resume('offer');
      }
    }
  }
  customElements.define('evolved-cart-offer', EvolvedCartOffer);
  // A removed qualifying product must not leave a previously free add-on behind.
  const cleanOrphanedGifts = () => {
    enqueue(async () => {
      const cart = await request('cart.js');
      const orphaned = cart.items.filter(item => item.properties?._ev_gift_for && !cart.items.some(parent => String(parent.product_id) === item.properties._ev_gift_for && parent.properties?._ev_offer_token === item.properties._ev_offer_token));
      if (!orphaned.length) return;
      const updated = await request('cart/update.js', { updates: Object.fromEntries(orphaned.map(item => [item.key, 0])) });
      await refresh(updated);
    }).catch(() => document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })));
  };
  document.addEventListener('cart:change', event => {
    if (event.detail?.cart?.items?.length === 0) {
      const key = getStored('ev-offer-active-timer-v1', true);
      if (key) putStored(key, '0', true);
      putStored('ev-offer-active-timer-v1', '', true);
    }
    const items = event.detail?.cart?.items || [];
    if (items.some(item => item.properties?._ev_gift_for)) cleanOrphanedGifts();
  });
  if (document.querySelector('[data-ev-orphan-gift]')) cleanOrphanedGifts();
  // Native quantity edits and page reloads also need to retain accepted offers.
  // A settled cart does not write or re-render again.
  let discountTimer;
  const settleDiscounts = () => {
    clearTimeout(discountTimer);
    discountTimer = setTimeout(() => {
      if (accepting || window.EvolvedMystery?.isBusy() || window.EvolvedWelcomeGift?.inspect?.().busy || document.querySelector('line-item-quantity[aria-busy="true"], product-form [aria-busy="true"]')) { settleDiscounts(); return; }
      enqueue(async () => {
        const before = await rawRequest('cart.js');
        if (!window.EvolvedCartDiscounts?.needsRepair(before)) return;
        await window.EvolvedShippingProtection?.pause('discounts');
        try {
          const after = await window.EvolvedCartDiscounts.restore(before, rawRequest);
          if (JSON.stringify([before.total_price,codes(before)]) !== JSON.stringify([after.total_price,codes(after)])) await refresh(after);
        } finally { window.EvolvedShippingProtection?.resume('discounts'); }
      }).catch(() => {});
    }, 250);
  };
  document.addEventListener('cart:change', event => {
    if (window.EvolvedCartDiscounts?.needsRepair(event.detail?.cart || {items:[]})) settleDiscounts();
  });
  const checkExistingOffers = () => {
    if ([...document.querySelectorAll('[data-line-properties]')].some(node => node.dataset.lineProperties.includes('_ev_offer_code'))) settleDiscounts();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkExistingOffers, {once:true});
  else checkExistingOffers();
  window.addEventListener('pageshow', event => { if (event.persisted) checkExistingOffers(); });
})();

